import { useState } from "react";
import { ColumnDef } from "@tanstack/react-table";
import { Column } from "@components/EditableDataTable/types/column";
import { useV2ListResourcesForAccountQuery } from "@generated";
import BlueprintItemsTab, { TabRow } from "./BlueprintItemsTab";
import type { BlueprintDetail } from "../../common/api";

const tabColumns: Column[] = [
  { header: () => "Name", cell: (row) => row.name },
  { header: () => "Hourly Rate", cell: (row) => row.hourlyRate },
  { header: () => "Hourly Cost", cell: (row) => row.hourlyCost },
];

const catalogColumns: ColumnDef<TabRow>[] = [
  { header: "Name", cell: (props) => props.row.original.name },
  { header: "Hourly Rate", accessorKey: "hourlyRate" },
  { header: "Hourly Cost", accessorKey: "hourlyCost" },
];

const toTabRow = (item: any, idx: number): TabRow => ({
  id: String(item.id ?? item.lid ?? `r_${idx}`),
  name: item.attributes?.name || "",
  hourlyRate: item.attributes?.["hourly-rate"] ?? "",
  hourlyCost: item.attributes?.["hourly-cost"] ?? "",
});

const toPayloadItem = (row: TabRow) => ({
  lid: row.id,
  type: "project-resources",
  attributes: {
    name: row.name,
    "hourly-rate": row.hourlyRate,
    "hourly-cost": row.hourlyCost,
  },
  relationships: {
    resource: { data: { id: Number(row.id), type: "resources" } },
  },
});

const ResourcesTab = ({
  blueprint,
  refetch,
  readOnly,
}: {
  blueprint: BlueprintDetail;
  refetch: () => void;
  readOnly?: boolean;
}) => {
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState("");

  const { data, isLoading } = useV2ListResourcesForAccountQuery({
    filter: { active: true, name: search || undefined } as any,
    page: { number: page, size: pageSize },
  });

  const catalogItems: TabRow[] = (data?.data ?? []).map((r) => ({
    id: String(r.id),
    name: r.attributes?.name || "",
    hourlyRate: r.attributes?.["hourly-rate"] ?? "",
    hourlyCost: r.attributes?.["hourly-cost"] ?? "",
  }));

  return (
    <BlueprintItemsTab
      blueprint={blueprint}
      refetch={refetch}
      payloadType="project-resources"
      toTabRow={toTabRow}
      toPayloadItem={toPayloadItem}
      tabColumns={tabColumns}
      catalogColumns={catalogColumns}
      catalogItems={catalogItems}
      catalogLoading={isLoading}
      onCatalogSearch={(q) => {
        setSearch(q);
        setPage(1);
      }}
      catalogPagination={{
        currentPage: page,
        pageSize,
        totalPages: (data as any)?.meta?.["page-count"] ?? 1,
        totalRows: (data as any)?.meta?.["record-count"] ?? 0,
        setCurrentPage: setPage,
        setPageSize,
      }}
      itemLabel="Resources"
      readOnly={readOnly}
    />
  );
};

export default ResourcesTab;
